// The workpaper picker shared by both Optro import wizards: pick from the
// candidates the caller supplies, upload your own as a fallback, or apply
// nothing at all. Controls feeds it a control's linked workpapers
// (`useOptroLinkedWorkpapers`); work steps feed it an ops-audit item's files
// and attachments (`useOptroWorkStepWorkpapers`). The candidate fetch lives
// with the caller precisely because the two are keyed differently — a control
// by external ref, a work step by numeric item datum id.
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, RefreshCw, Upload, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { Button } from "../../../../../components/ui/button";
import { Checkbox } from "../../../../../components/ui/checkbox";
import { cn } from "../../../../../lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../../components/ui/tooltip";
import { OptroWorkpaperFile } from "../../../../../models/audit/optroSync";
import {
  requestPresignedUrls,
  uploadFileToS3,
} from "../../../../../api/presignedUpload";

interface ChangeWorkpaperDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Identifies the row being edited; a change re-seeds the draft selection.
  targetKey: string | null;
  // Pickable candidates for `targetKey`, and whether they are still loading.
  candidates: OptroWorkpaperFile[];
  candidatesLoading: boolean;
  title: string;
  description: string;
  // Shown when there is nothing to pick and nothing uploaded yet. Owned by
  // the caller because an empty candidate list means different things on the
  // two sides — see the work-step review page.
  emptyMessage: string;
  // What is checked for `targetKey`. Read at open and again once the
  // candidate fetch settles, so a caller that derives it from the candidates
  // (the work-step "all Optro files" default) sees them pre-checked.
  current: OptroWorkpaperFile[];
  onApply: (files: OptroWorkpaperFile[]) => void;
  // Optional footer action handed the draft selection as the dialog closes.
  // The work-step wizard uses it to fall back to "all Optro files" while
  // keeping whatever was uploaded in the dialog.
  resetAction?: {
    label: string;
    onSelect: (chosen: OptroWorkpaperFile[]) => void;
  };
  // Optional footer button that refetches the candidates from Optro, for a
  // caller that caches them; `refreshing` spins it while the refetch runs.
  onRefresh?: () => void;
  refreshing?: boolean;
}

export const ChangeWorkpaperDialog = ({
  open,
  onOpenChange,
  targetKey,
  candidates,
  candidatesLoading,
  title,
  description,
  emptyMessage,
  current,
  onApply,
  resetAction,
  onRefresh,
  refreshing = false,
}: ChangeWorkpaperDialogProps) => {
  const [chosen, setChosen] = useState<OptroWorkpaperFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // The draft is cleared on close and absorbs `current` on open and again
  // when the candidate fetch settles — merging rather than replacing, so an
  // upload made while candidates were still loading survives.
  useEffect(() => {
    if (!open || !targetKey) {
      setChosen([]);
      return;
    }
    if (candidatesLoading) return;
    setChosen((prev) => [
      ...prev,
      ...current.filter((file) => !prev.some((f) => f.id === file.id)),
    ]);
    // `current` is intentionally read only at those two moments.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetKey, candidatesLoading]);

  const toggle = (file: OptroWorkpaperFile) => {
    setChosen((prev) =>
      prev.some((f) => f.id === file.id)
        ? prev.filter((f) => f.id !== file.id)
        : [...prev, file],
    );
  };

  const onFilesPicked = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    setUploading(true);
    try {
      const presignedItems = await requestPresignedUrls(
        files.map((file) => ({
          filename: file.name,
          contentType: file.type || "application/octet-stream",
        })),
      );
      await Promise.all(
        files.map((file, index) =>
          uploadFileToS3(
            file,
            presignedItems[index].presignedUrl,
            file.type || "application/octet-stream",
            presignedItems[index].requiresServerSideEncryption,
          ),
        ),
      );
      const uploads: OptroWorkpaperFile[] = files.map((file, index) => ({
        id: presignedItems[index].uploadId,
        fileName: file.name,
        source: "upload",
        period: null,
        fileId: null,
      }));
      setChosen((prev) => [...prev, ...uploads]);
    } catch (error) {
      console.error("Failed to upload workpaper file:", error);
      toast.error("Failed to upload file. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {candidatesLoading ? (
          <div className="py-10 text-center">
            <Loader2 className="mx-auto h-5 w-5 animate-spin text-stone-400" />
          </div>
        ) : (
          <div
            aria-busy={refreshing}
            className={cn(
              "space-y-1 max-h-64 overflow-y-auto border rounded-md p-2",
              // One sweep across the whole list, as on rows still importing.
              refreshing && "gradient-sweep-row text-stone-400",
            )}
          >
            {candidates.length === 0 && chosen.length === 0 ? (
              <p className="py-8 text-center text-sm text-stone-500">
                {emptyMessage}
              </p>
            ) : (
              <>
                {candidates.map((file) => (
                  <label
                    key={file.id}
                    className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-stone-50 cursor-pointer"
                  >
                    <Checkbox
                      checked={chosen.some((f) => f.id === file.id)}
                      onCheckedChange={() => toggle(file)}
                    />
                    <FileText className="h-4 w-4 text-stone-500" />
                    <span className="text-sm flex-1">{file.fileName}</span>
                    {file.period && (
                      <span className="text-xs text-stone-400">
                        {file.period}
                      </span>
                    )}
                  </label>
                ))}
                {/* Chosen files with no candidate row: uploads, and any Optro
                    file the list doesn't hold (deleted since, a list cached
                    before the match picked it, or a failed fetch) — so every
                    file Apply submits is visible and removable. */}
                {chosen
                  .filter(
                    (f) =>
                      f.source === "upload" ||
                      !candidates.some((c) => c.id === f.id),
                  )
                  .map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center gap-2 rounded px-2 py-1.5"
                    >
                      {file.source === "upload" ? (
                        <Upload className="h-4 w-4 text-stone-500" />
                      ) : (
                        <FileText className="h-4 w-4 text-stone-500" />
                      )}
                      <span className="text-sm flex-1">{file.fileName}</span>
                      <button
                        type="button"
                        onClick={() => toggle(file)}
                        aria-label={`Remove ${file.fileName}`}
                      >
                        <X className="h-4 w-4 text-stone-400" />
                      </button>
                    </div>
                  ))}
              </>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            void onFilesPicked(e.target.files);
            // Clear so re-picking the same file fires `change` again.
            e.target.value = "";
          }}
        />
        <Button
          variant="outline"
          className="w-fit"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Upload className="h-4 w-4 mr-2" />
          )}
          {uploading ? "Uploading..." : "Choose Files"}
        </Button>

        <DialogFooter>
          {(onRefresh || resetAction) && (
            <div className="flex gap-2 sm:mr-auto">
              {onRefresh && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Refresh from Optro"
                      disabled={candidatesLoading || refreshing || uploading}
                      onClick={onRefresh}
                    >
                      <RefreshCw
                        className={cn(
                          "h-4 w-4",
                          refreshing &&
                            "animate-spin motion-reduce:animate-none",
                        )}
                      />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Refresh from Optro</TooltipContent>
                </Tooltip>
              )}
              {resetAction && (
                <Button
                  variant="ghost"
                  disabled={uploading}
                  onClick={() => {
                    resetAction.onSelect(chosen);
                    onOpenChange(false);
                  }}
                >
                  {resetAction.label}
                </Button>
              )}
            </div>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            disabled={uploading}
            onClick={() => {
              onApply(chosen);
              onOpenChange(false);
            }}
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
